/* eslint-disable @typescript-eslint/no-explicit-any */

export default defineNuxtPlugin(() => {
  // We intercept at the NATIVE fetch level so that all high-level $fetch instances (including apiFetch with toaster)
  // are forced to go through our mocking logic.
  if (typeof window === 'undefined') return // Only client-side mocking for now

  // GUARD: Only allow mocking in development or test environments
  const config = useRuntimeConfig()
  if (!import.meta.dev && !process.env.TEST && !config.public.isTest) return

  const originalFetch = window.fetch

  const readCookie = (name: string) => {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${escapedName}=([^;]*)`)
    )

    if (!match) {
      return null
    }

    return decodeURIComponent(match[1] || '')
  }

  const getMockMode = () => readCookie('e2e-mock-mode')
  const getMockFeature = () => {
    const cookie = readCookie('e2e-mock-feature')
    if (cookie) return cookie

    // Fallback for manual browser usage: derive from path ("/glossary" -> "glossary")
    const segments = window.location.pathname.split('/').filter(Boolean)
    return segments[0] || null
  }
  const getManageUrl = () => {
    return `${window.location.origin}/api/mock-manage`
  }

  const parseResponseBody = async (response: Response) => {
    const clonedResponse = response.clone()

    try {
      return await clonedResponse.json()
    } catch {
      return await clonedResponse.text().catch(() => null)
    }
  }

  const persistMock = async (
    request: {
      url: string
      method: string
      headers: HeadersInit | undefined
      body: any
    },
    response: Response
  ) => {
    const responseStatus = response.status
    if (responseStatus === 404) {
      return { skipped: true }
    }

    const responseHeaders = {} as Record<string, string>
    response.headers.forEach((val, key) => {
      responseHeaders[key] = val
    })

    const responseBody = await parseResponseBody(response)

    const saveResponse = await originalFetch(getManageUrl(), {
      method: 'POST',
      headers: {
        'X-Mock-Bypass': 'true',
        'X-Mock-Secret': config.public.mockManagementSecret,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'write',
        request,
        feature: getMockFeature(),
        response: {
          status: responseStatus,
          headers: responseHeaders,
          body: responseBody,
        },
      }),
    })

    if (!saveResponse.ok) {
      throw new Error(
        `Save failed ${saveResponse.status}: ${saveResponse.statusText}`
      )
    }

    return { skipped: false }
  }

  const readMockViaManager = async (request: {
    url: string
    method: string
    headers: HeadersInit | undefined
    body: any
  }) => {
    const mockResponse = await originalFetch(getManageUrl(), {
      method: 'POST',
      headers: {
        'X-Mock-Bypass': 'true',
        'X-Mock-Secret': config.public.mockManagementSecret,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'read',
        request,
        feature: getMockFeature(),
      }),
    })

    if (!mockResponse.ok) {
      const errorPayload = await mockResponse.json().catch(() => null)
      const message = errorPayload?.message || mockResponse.statusText
      const details = errorPayload?.details
      let detailSuffix = ''
      if (details) {
        const featurePart = details.feature
          ? `, feature=${details.feature}`
          : ''
        detailSuffix = ` (hash=${details.originalHash}${featurePart})`
      }
      throw new Error(
        `Mock server returned ${mockResponse.status}: ${message}${detailSuffix}`
      )
    }

    const mock = await mockResponse.json()
    return new Response(JSON.stringify(mock.body), {
      status: mock.status,
      headers: new Headers(mock.headers),
      statusText: mock.status >= 400 ? 'Bad Request' : 'OK',
    })
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const mode = getMockMode()
    const urlStr = input.toString()
    const method = init?.method?.toUpperCase() || 'GET'

    // AUTO-RESPOND TO OPTIONS (PREFLIGHT)
    if (mode && mode !== 'none' && method === 'OPTIONS') {
      return new Response(null, {
        status: 204,
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods':
            'GET, POST, PUT, DELETE, OPTIONS, PATCH',
          'Access-Control-Allow-Headers': '*',
          'Access-Control-Max-Age': '86400',
        },
      })
    }

    // FILTER: Ignore internal Nuxt/Vite/Mock-Manage requests and external noise
    if (
      urlStr.startsWith('/_nuxt') ||
      urlStr.startsWith('/@vite') ||
      urlStr.includes('/node_modules/') ||
      urlStr.includes('hot-update') ||
      urlStr.includes('/api/mock-manage') ||
      urlStr.includes('/api/billing/') ||
      urlStr.includes('/auth/v2/groups/logo') ||
      urlStr.includes('storage.googleapis.com') ||
      urlStr.includes('iconify.design') ||
      urlStr.includes('segment.io') || // Common analytics
      urlStr.includes('/auth/realms/') // Keycloak redirects
    ) {
      return originalFetch(input, init)
    }

    if (
      !mode ||
      mode === 'none' ||
      (init?.headers && (init.headers as any)['X-Mock-Bypass'])
    ) {
      return originalFetch(input, init)
    }

    // Streaming responses (SSE) cannot be replayed by the JSON-based mock
    // manager, and any URL prefix listed in the `e2e-mock-bypass-prefixes`
    // cookie is delegated to the real network so that Playwright's
    // `page.route` can intercept it deterministically per-test.
    const acceptHeader = (() => {
      const headers = init?.headers as Record<string, string> | undefined
      if (!headers) return ''
      return headers.accept || headers.Accept || ''
    })()
    const bypassPatterns = (readCookie('e2e-mock-bypass-patterns') || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
    if (
      acceptHeader.includes('text/event-stream') ||
      bypassPatterns.some((pattern) => urlStr.includes(pattern))
    ) {
      return originalFetch(input, init)
    }

    // Prepare mock request data for hashing
    let bodyData: any = init?.body
    if (bodyData && typeof bodyData === 'string') {
      try {
        bodyData = JSON.parse(bodyData)
      } catch {
        // Not JSON, keep as string
      }
    }

    const mockReq = {
      url: urlStr,
      method: init?.method || 'GET',
      headers: init?.headers || {},
      body: bodyData,
    }

    const strictReadError = (err: unknown) => {
      const message = String((err as { message?: string })?.message || err)
      return new Error(
        `[Mock] Strict playback miss for ${method} ${urlStr}. ${message}`
      )
    }

    if (mode === 'read') {
      try {
        const response = await readMockViaManager(mockReq)
        console.log(`[Mock Read] hit ${method} ${urlStr}`)
        return response
      } catch (err) {
        console.error(`[Mock Read] miss ${method} ${urlStr}`)
        throw strictReadError(err)
      }
    }

    if (mode === 'read-through') {
      try {
        const response = await readMockViaManager(mockReq)
        console.log(`[Mock ReadThrough] hit ${method} ${urlStr}`)
        return response
      } catch (err) {
        console.warn(
          `[Mock ReadThrough] miss ${method} ${urlStr}. Falling back to live request. ${String((err as { message?: string })?.message || err)}`
        )
      }

      try {
        const response = await originalFetch(input, init)
        try {
          const saveResult = await persistMock(mockReq, response)
          if (saveResult.skipped) {
            console.warn(
              `[Mock ReadThrough] live 404 not saved for ${method} ${urlStr}`
            )
          } else {
            console.log(`[Mock ReadThrough] saved ${method} ${urlStr}`)
          }
        } catch (saveErr) {
          console.error('[Mock ReadThrough] Save failed', saveErr)
        }

        return response
      } catch (err) {
        console.error('[Mock ReadThrough] Live request failed:', err)
        throw err
      }
    }

    if (mode === 'write') {
      try {
        const response = await originalFetch(input, init)
        try {
          const saveResult = await persistMock(mockReq, response)
          if (saveResult.skipped) {
            console.warn(
              `[Mock Write] live 404 not saved for ${method} ${urlStr}`
            )
          }
        } catch (saveErr) {
          console.error('[Mock Write] Save failed', saveErr)
        }

        console.log('[Mock Write]', method, urlStr)
        return response
      } catch (err) {
        console.error('[Mock Write] Live request failed:', err)
        throw err
      }
    }

    return originalFetch(input, init)
  }
})
